import { Container } from "@mui/material";
import "./faq.scss";
import Grid from "@mui/material/Grid2";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [thinkingIndex, setThinkingIndex] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [sources, setSources] = useState([]);

  const [sessionId] = useState(() => crypto.randomUUID());
  const [history, setHistory] = useState([]);

  const qaData = [
    {
      question: "What services does Utilitarian Labs offer?",
      answer:
        "Utilitarian Labs provides consulting and implementation services across **Salesforce solutions, data analytics, and digital transformation initiatives.** We help organizations streamline processes, improve customer engagement, and leverage data-driven insights to drive business growth.",
    },
    {
      question: "How can Utilitarian Labs help our business with Salesforce?",
      answer:
        "Our team helps organizations **implement, customize, and optimize Salesforce** to align with their business processes. This includes CRM implementation, automation of sales and service workflows, system integrations, and analytics solutions that help teams make better decisions.",
    },
    {
      question:
        "Do you support ongoing maintenance and support after implementation?",
      answer:
        "Yes. We offer **Annual Maintenance Support (AMS)** that include system support, enhancements, performance monitoring, and issue resolution to ensure your Salesforce environment runs smoothly and optimally",
    },
  ];

  // STATIC QUESTION CLICK
  const handleStaticClick = (index) => {
    if (activeIndex === index) {
      // clicking same Q again — close everything, show it back
      setActiveIndex(null);
      setSearchValue("");
      setAiAnswer("");
      setAiOpen(false);
      // setSources([]);
      return;
    }

    setActiveIndex(index);
    setSearchValue(qaData[index].question);
    setAiAnswer(qaData[index].answer);
    setAiOpen(true);
    setThinkingIndex(null);
    // setSources([]);
  };

  // AI SEARCH
  const handleSearch = async () => {
    if (!searchValue.trim()) return;

    setActiveIndex(null); // show all static Qs back
    setAiOpen(true);
    setThinkingIndex(0);
    setAiAnswer("");
    setSources([]);

    try {
      const res = await fetch(
        "https://utilchatbot-7b51bda00fd0.herokuapp.com/chat",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message: searchValue,
            session_id: sessionId,
            history: history.slice(-8),
          }),
        },
      );

      if (!res.ok) throw new Error("Request failed");

      const data = await res.json();
      console.log("reply", data.reply);
      console.log("sources", data.sources);

      setThinkingIndex(null);
      setAiAnswer(data.reply);
      setSources(data.sources || []);

      setHistory((prev) => [
        ...prev,
        { role: "user", content: searchValue },
        { role: "assistant", content: data.reply },
      ]);
    } catch (err) {
      setThinkingIndex(null);
      setAiAnswer("Something went wrong. Please try again.");
      console.log("error", err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  // CLOSE AI ANSWER BOX
  const handleAiToggle = () => {
    setAiOpen(false);
    setAiAnswer("");
    setThinkingIndex(null);
    setSearchValue("");
    setSources([]);
    setActiveIndex(null); // show all static Qs back
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setActiveIndex(null);
    setAiAnswer("");
    setAiOpen(false);
    setThinkingIndex(null);
  };

  // IMAGE LOGIC
  const getImage = () => {
    if (thinkingIndex !== null) return "/images/icon_thinking.webp";
    if (aiAnswer) return "/images/icon_answer.webp";
    return "/images/icon_hi.webp";
  };

  return (
    <div className="faq">
      <Container>
        <Grid container justifyContent="center" alignItems="center" gap="85px">
          {/* IMAGE */}
          <Grid
            size={{ xs: 12, md: 2.5 }}
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              minHeight: "350px",
              aspectRatio: "1 / 1",
              display: { xs: "none", md: "flex" },
            }}
          >
             <img  loading="lazy" src={getImage()} alt="" />
          </Grid>

          {/* CONTENT */}
          <Grid
            size={{ xs: 12, md: 8 }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <h1 className="faq_head">Ask Me Anything</h1>

            <div className="faq_ques">
              {/* SEARCH */}
              <p className="faq_search_ques faq_question">
                <SearchRoundedIcon onClick={handleSearch} />
                <input
                  className="faq_search_input"
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Hello! How may I help you?"
                />
                {searchValue && (
                  <span className="faq_close" onClick={handleClearSearch}>
                    ✕
                  </span>
                )}
              </p>

              {/* AI ANSWER BOX */}
              {aiOpen && (
                <p className="faq_question active">
                  {/* <span className="faq_close" onClick={handleAiToggle}>
                    ✕
                  </span> */}
                  <span className="faq_text">
                    {thinkingIndex === 0 ? (
                      "Thinking..."
                    ) : (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkBreaks]}
                        components={{
                          a: ({ node, ...props }) => (
                            <a {...props} target="_blank" rel="noreferrer" />
                          ),
                        }}
                      >
                        {aiAnswer}
                      </ReactMarkdown>
                    )}
                  </span>
                </p>
              )}

              {/* STATIC QUESTIONS — hidden when active, visible otherwise */}
              {qaData.map(
                (item, index) =>
                  activeIndex !== index && (
                    <p
                      key={index}
                      className="faq_question"
                      onClick={() => handleStaticClick(index)}
                    >
                      <span className="faq_text">{item.question}</span>
                    </p>
                  ),
              )}
            </div>

            <p className="faq_bottom">
              This chatbot is powered by Utilitarian Labs. Responses are
              AI-generated and may be inaccurate—please verify critical
              information. Personal data may be collected in accordance with our
              policies.
            </p>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default FAQ;
